import type { SelectOption } from "@/src/constants/documentPreparationPopUpConsts";

export type IntakeMonth = "february" | "september";

export type IntakeYearGroupKey = "next-2" | "next-1" | "current" | "previous-1" | "previous-2";

export interface IntakeOptionGroup {
  key: IntakeYearGroupKey | "stored";
  title: string;
  options: SelectOption[];
}

export interface IntakeSelectGroup {
  title: string;
  items: string[];
}

const INTAKE_MONTH_LABEL: Record<IntakeMonth, string> = {
  february: "February",
  september: "September",
};

export const INTAKE_YEAR_GROUP_LABELS: Record<IntakeYearGroupKey, string> = {
  "next-2": "Next 2",
  "next-1": "Next year",
  current: "Current",
  "previous-1": "Previous",
  "previous-2": "Previous 2",
};

const INTAKE_YEAR_OFFSETS: { key: IntakeYearGroupKey; offset: number }[] = [
  { key: "next-2", offset: 2 },
  { key: "next-1", offset: 1 },
  { key: "current", offset: 0 },
  { key: "previous-1", offset: -1 },
  { key: "previous-2", offset: -2 },
];

export const buildIntakeOptionValue = (month: IntakeMonth, year: number): string => `${month}-${year}`;

export const buildIntakeOptionLabel = (month: IntakeMonth, year: number): string =>
  `${INTAKE_MONTH_LABEL[month]} ${year}`;

const buildIntakeOptionsForYear = (year: number): SelectOption[] => [
  {
    value: buildIntakeOptionValue("september", year),
    label: buildIntakeOptionLabel("september", year),
  },
  {
    value: buildIntakeOptionValue("february", year),
    label: buildIntakeOptionLabel("february", year),
  },
];

/** Current year ±2; each group has September and February (newest groups first). */
export const buildIntakeOptionGroups = (referenceDate: Date = new Date()): IntakeOptionGroup[] => {
  const centerYear = referenceDate.getFullYear();

  return INTAKE_YEAR_OFFSETS.map(({ key, offset }) => ({
    key,
    title: INTAKE_YEAR_GROUP_LABELS[key],
    options: buildIntakeOptionsForYear(centerYear + offset),
  }));
};

/** Flat list of all intake options (10 by default). */
export const buildIntakeOptions = (referenceDate: Date = new Date()): SelectOption[] =>
  buildIntakeOptionGroups(referenceDate).flatMap((group) => group.options);

export const mapIntakeGroupsToSelectGroups = (groups: IntakeOptionGroup[]): IntakeSelectGroup[] =>
  groups
    .filter((group) => group.options.length > 0)
    .map(({ title, options }) => ({
      title,
      items: options.map((option) => option.label),
    }));

const resolveDefaultIntakeMonth = (month: number): IntakeMonth => {
  const inDecemberToJuly = month === 12 || month <= 7;
  const inJulyToDecember = month >= 7;

  if (inDecemberToJuly && inJulyToDecember) {
    return month === 12 ? "february" : "september";
  }

  return inDecemberToJuly ? "february" : "september";
};

const resolveDefaultIntakeYear = (intakeMonth: IntakeMonth, calendarMonth: number, year: number): number => {
  if (intakeMonth === "september") {
    return year;
  }

  if (calendarMonth === 12 || calendarMonth >= 3) {
    return year + 1;
  }

  return year;
};

/**
 * Default intake from reference date:
 * - Dec through Jul (inclusive) → February
 * - Jul through Dec (inclusive) → September
 * Overlap: Jul → September, Dec → February
 */
export const resolveDefaultIntakeValue = (referenceDate: Date = new Date()): string => {
  const month = referenceDate.getMonth() + 1;
  const year = referenceDate.getFullYear();
  const intakeMonth = resolveDefaultIntakeMonth(month);
  const intakeYear = resolveDefaultIntakeYear(intakeMonth, month, year);

  return buildIntakeOptionValue(intakeMonth, intakeYear);
};

export const intakeValueToLabel = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  const slugMatch = trimmed.match(/^(february|september)-(\d{4})$/i);
  if (slugMatch) {
    return buildIntakeOptionLabel(slugMatch[1].toLowerCase() as IntakeMonth, Number(slugMatch[2]));
  }

  return trimmed;
};

const parseIntakeLabel = (label: string): SelectOption | null => {
  const match = label.trim().match(/^(February|September)\s+(\d{4})$/i);
  if (!match) return null;

  const month = match[1].toLowerCase() as IntakeMonth;
  const year = Number(match[2]);
  if (!Number.isFinite(year)) return null;

  return {
    value: buildIntakeOptionValue(month, year),
    label: buildIntakeOptionLabel(month, year),
  };
};

/** Maps stored task/API value or label to internal intake slug. */
export const resolveIntakeOptionValue = (
  stored: string | null | undefined,
  options: SelectOption[] = buildIntakeOptions(),
): string => {
  const trimmed = (stored ?? "").trim();
  if (!trimmed) return "";

  const byValue = options.find((option) => option.value === trimmed);
  if (byValue) return byValue.value;

  const byLabel = options.find((option) => option.label === trimmed);
  if (byLabel) return byLabel.value;

  const parsed = parseIntakeLabel(trimmed);
  return parsed?.value ?? trimmed;
};

export const getIntakeDisplayLabel = (value: string, options: SelectOption[] = buildIntakeOptions()): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  const fromOptions = options.find((option) => option.value === trimmed)?.label;
  if (fromOptions) return fromOptions;

  const fromLabel = options.find((option) => option.label === trimmed)?.label;
  if (fromLabel) return fromLabel;

  return intakeValueToLabel(trimmed);
};

/** Ensures a stored intake outside the default 5-year window still appears in the dropdown. */
export const buildIntakeOptionsWithStored = (
  stored?: string | null,
  referenceDate: Date = new Date(),
): SelectOption[] => {
  const groups = buildIntakeOptionGroupsWithStored(stored, referenceDate);
  return groups.flatMap((group) => group.options);
};

export const buildIntakeOptionGroupsWithStored = (
  stored?: string | null,
  referenceDate: Date = new Date(),
): IntakeOptionGroup[] => {
  const groups = buildIntakeOptionGroups(referenceDate);
  const options = groups.flatMap((group) => group.options);
  const resolved = resolveIntakeOptionValue(stored, options);
  if (!resolved) return groups;

  if (options.some((option) => option.value === resolved)) {
    return groups;
  }

  const parsed = parseIntakeLabel(stored ?? "") ?? {
    value: resolved,
    label: intakeValueToLabel(resolved),
  };

  if (!parsed.label.trim()) return groups;

  return [
    {
      key: "stored",
      title: "",
      options: [parsed],
    },
    ...groups,
  ];
};
